import React, { useRef, useEffect, useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import katex from 'katex';
import CitationRenderer from './CitationRenderer';
import { handleSourceClick } from '../utils/fileDownload';
import { showError } from '../utils/toastUtils';

const MarkdownWithCitations = ({ content, sources = [] }) => {
    const renderRef = useRef(null);

    const processedData = useMemo(() => {
        if (!content) return { html: '', citationPlaceholders: [], mathPlaceholders: [] };

        const citationPlaceholders = [];
        const mathPlaceholders = [];
        let tempContent = content;

        const sourceIds = new Set((sources || []).map(s => String(s.id)));

        tempContent = tempContent.replace(/\[(\d+)\]/g, (match, citationNumber) => {
            return sourceIds.has(citationNumber) ? match : '';
        });

        if (sourceIds.size > 0) {
            tempContent = tempContent.replace(/(\[(\d+)\])+/g, (match) => {
                citationPlaceholders.push(match);
                return `<span class="citation-placeholder" data-id="${citationPlaceholders.length - 1}"></span>`;
            });
        }

        tempContent = tempContent.replace(/(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\])/g, (match) => {
            const formulaContent = match.slice(2, -2);
            mathPlaceholders.push({ type: 'display', content: formulaContent });
            return `<div class="katex-placeholder" data-id="${mathPlaceholders.length - 1}"></div>`;
        });

        tempContent = tempContent.replace(/(\s|^)\$([^ $][\s\S]*?[^ $])\$(\s|$)/g, (match, p1, p2, p3) => {
            mathPlaceholders.push({ type: 'inline', content: p2 });
            return `${p1}<span class="katex-placeholder" data-id="${mathPlaceholders.length - 1}"></span>${p3}`;
        });

        tempContent = tempContent.replace(/\\\([\s\S]*?\\\)/g, (match) => {
            const formulaContent = match.slice(2, -2);
            mathPlaceholders.push({ type: 'inline', content: formulaContent });
            return `<span class="katex-placeholder" data-id="${mathPlaceholders.length - 1}"></span>`;
        });

        marked.setOptions({ breaks: true, gfm: true });
        const rawHtml = marked.parse(tempContent);

        const htmlWithCustomBr = rawHtml.replace(/<br\s*\/?>/gi, '<div class="custom-br-spacing"></div>');

        const sanitizedHtml = DOMPurify.sanitize(htmlWithCustomBr, {
            USE_PROFILES: { html: true },
            ADD_ATTR: ['data-id', 'class', 'style']
        });

        return { html: sanitizedHtml, citationPlaceholders, mathPlaceholders };
    }, [content, sources]);

    useEffect(() => {
        if (!renderRef.current || !processedData.html) return;

        renderRef.current.innerHTML = processedData.html;

        renderRef.current.querySelectorAll('table').forEach(table => {
            if (table.parentElement?.classList?.contains('table-scroll')) return;
            const wrapper = document.createElement('div');
            wrapper.className = 'overflow-x-auto table-scroll my-3 rounded-2xl border border-[#ececed] overflow-hidden';
            wrapper.style.cssText = '-webkit-overflow-scrolling: touch;';
            table.parentNode.insertBefore(wrapper, table);
            wrapper.appendChild(table);
            table.style.tableLayout = 'auto';
            table.querySelectorAll('th, td').forEach(cell => {
                const text = cell.textContent.trim();
                const hasLongText = text.length > 50;
                cell.style.maxWidth = '400px';
                cell.style.whiteSpace = hasLongText ? 'normal' : 'nowrap';
                if (hasLongText) cell.style.overflowWrap = 'break-word';
            });
        });

        renderRef.current.querySelectorAll('.katex-placeholder').forEach(el => {
            const id = parseInt(el.dataset.id, 10);
            if (processedData.mathPlaceholders[id]) {
                katex.render(processedData.mathPlaceholders[id].content, el, {
                    throwOnError: false,
                    displayMode: processedData.mathPlaceholders[id].type === 'display'
                });
            }
        });

        renderRef.current.querySelectorAll('.citation-placeholder').forEach(el => {
            const id = parseInt(el.dataset.id, 10);
            if (processedData.citationPlaceholders[id]) {
                const citationDiv = document.createElement('span');
                el.parentNode.replaceChild(citationDiv, el);

                citationDiv.className = 'citation-marker';
                citationDiv.setAttribute('data-citation', processedData.citationPlaceholders[id]);
            }
        });
    }, [processedData]);

    useEffect(() => {
        if (!renderRef.current) return;

        document.querySelectorAll('.citation-tooltip').forEach(t => t.remove());

        const citationMarkers = renderRef.current.querySelectorAll('.citation-marker');
        const eventListeners = [];

        citationMarkers.forEach(marker => {
            const citationText = marker.getAttribute('data-citation');
            const citationNumbers = (citationText.match(/\d+/g) || []).map(Number);

            if (citationNumbers.length === 0) return;

            const citationSources = citationNumbers
                .map(num => sources.find(s => parseInt(s.id, 10) === num))
                .filter(Boolean);

            if (citationSources.length === 0) return;

            const firstSource = citationSources[0];
            const remainingCount = citationSources.length - 1;

            const citationSpan = document.createElement('span');
            citationSpan.className = 'citation-group inline-block ml-[0.5px] align-baseline';

            const button = document.createElement('button');
            button.className = 'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-citation-bg text-[#5d5d5d] hover:bg-[#e8e8e8] h-4 px-1.5 py-0.5 text-[9px] align-baseline -translate-y-[2px]';

            const displayName = firstSource.name || 'source';
            button.textContent = remainingCount > 0 ? `${displayName} +${remainingCount}` : displayName;
            citationSpan.appendChild(button);

            let tooltip = null;
            let hideTooltipTimer = null;
            let showTooltipTimer = null;

            const showTooltip = (e) => {
                clearTimeout(hideTooltipTimer);
                clearTimeout(showTooltipTimer);

                showTooltipTimer = setTimeout(() => {
                    if (!document.body.contains(e.target)) {
                        return;
                    }

                    if (tooltip) tooltip.remove();

                    tooltip = document.createElement('div');
                    tooltip.className = 'citation-tooltip fixed z-50 bg-app-bg border border-app-border rounded-lg shadow-lg p-3 max-w-xs animate-fadeIn';

                    const isSingleSource = citationSources.length === 1;

                    if (!isSingleSource) {
                        const header = document.createElement('div');
                        header.className = 'text-xs text-gray-500 pb-2 mb-2';
                        header.textContent = `Sources · ${citationSources.length}`;
                        tooltip.appendChild(header);
                    }

                    const sourceList = document.createElement('div');
                    sourceList.className = 'space-y-2 max-h-80 overflow-y-auto pr-2';
                    tooltip.appendChild(sourceList);

                    citationSources.forEach(source => {
                        const isFile = source.type === 'file' || source.metadata?.action === 'download';
                        const sourceItem = document.createElement(isFile ? 'div' : 'a');
                        sourceItem.className = 'block p-1.5 rounded-md hover:bg-gray-50 cursor-pointer';

                        if (isFile) {
                            sourceItem.addEventListener('click', async (e) => {
                                e.preventDefault();
                                e.stopPropagation();

                                if (tooltip) {
                                    tooltip.remove();
                                    tooltip = null;
                                }

                                try {
                                    await handleSourceClick(source, (error) => {
                                        showError(error.message || 'Không thể tải file. Vui lòng thử lại.');
                                    });
                                } catch (error) {
                                    showError('Lỗi khi tải file: ' + (error.message || 'Unknown error'));
                                }
                            });
                        } else {
                            sourceItem.href = source.url;
                            sourceItem.target = '_blank';
                            sourceItem.rel = 'noopener noreferrer';
                        }

                        const sourceHeader = document.createElement('div');
                        sourceHeader.className = 'flex items-center gap-2';

                        if (source.favicon) {
                            const favicon = document.createElement('img');
                            favicon.src = source.favicon;
                            favicon.alt = '';
                            favicon.className = 'w-4 h-4 flex-shrink-0';
                            favicon.onerror = (e) => { e.target.style.display = 'none'; };
                            sourceHeader.appendChild(favicon);
                        }

                        if (source.domain) {
                            const sourceDomain = document.createElement('div');
                            sourceDomain.className = 'text-xs text-gray-500';
                            sourceDomain.textContent = source.domain;
                            sourceHeader.appendChild(sourceDomain);
                        }

                        sourceItem.appendChild(sourceHeader);

                        const sourceInfo = document.createElement('div');
                        sourceInfo.className = 'mt-1';

                        const title = document.createElement('div');
                        title.className = 'font-medium text-sm text-gray-800 line-clamp-2';
                        title.textContent = source.title;
                        sourceInfo.appendChild(title);

                        if (isSingleSource && source.snippet) {
                            const snippet = document.createElement('div');
                            snippet.className = 'text-xs text-gray-600 mt-1 line-clamp-2';
                            snippet.textContent = source.snippet;
                            sourceInfo.appendChild(snippet);
                        }

                        sourceItem.appendChild(sourceInfo);
                        sourceList.appendChild(sourceItem);
                    });

                    tooltip.style.visibility = 'hidden';
                    document.body.appendChild(tooltip);

                    const rect = e.target.getBoundingClientRect();
                    const tooltipRect = tooltip.getBoundingClientRect();
                    const tooltipWidth = tooltipRect.width;
                    const tooltipHeight = tooltipRect.height;
                    const margin = 8;

                    let left = rect.left;
                    if (left < 10) left = 10;
                    if (left + tooltipWidth > window.innerWidth - 10) {
                        left = window.innerWidth - tooltipWidth - 10;
                    }
                    tooltip.style.left = `${left}px`;

                    const spaceBottom = window.innerHeight - rect.bottom;
                    const spaceTop = rect.top;

                    if (spaceBottom >= tooltipHeight + margin) {
                        tooltip.style.top = `${rect.bottom + margin}px`;
                    } else if (spaceTop >= tooltipHeight + margin) {
                        tooltip.style.top = `${rect.top - tooltipHeight - margin}px`;
                    } else {
                        tooltip.style.top = `${rect.bottom + margin}px`;
                    }

                    tooltip.style.visibility = 'visible';

                    const tooltipMouseEnter = () => {
                        clearTimeout(hideTooltipTimer);
                    };
                    const tooltipMouseLeave = () => {
                        hideTooltip();
                    };

                    tooltip.addEventListener('mouseenter', tooltipMouseEnter);
                    tooltip.addEventListener('mouseleave', tooltipMouseLeave);

                    eventListeners.push({ element: tooltip, event: 'mouseenter', handler: tooltipMouseEnter });
                    eventListeners.push({ element: tooltip, event: 'mouseleave', handler: tooltipMouseLeave });
                }, 100);
            };

            const hideTooltip = () => {
                clearTimeout(showTooltipTimer);
                clearTimeout(hideTooltipTimer);
                hideTooltipTimer = setTimeout(() => {
                    if (tooltip) {
                        tooltip.remove();
                        tooltip = null;
                    }
                }, 100);
            };

            button.addEventListener('mouseenter', showTooltip);
            button.addEventListener('mouseleave', hideTooltip);

            eventListeners.push({ element: button, event: 'mouseenter', handler: showTooltip });
            eventListeners.push({ element: button, event: 'mouseleave', handler: hideTooltip });

            marker.parentNode.replaceChild(citationSpan, marker);
        });

        return () => {
            eventListeners.forEach(({ element, event, handler }) => {
                element.removeEventListener(event, handler);
            });
            document.querySelectorAll('.citation-tooltip').forEach(t => t.remove());
        };
    }, [processedData, sources]);

    return (
        <div
            ref={renderRef}
            className="text-[16px] prose max-w-none
                 prose-p:my-2 prose-p:leading-[1.75] prose-p:text-gray-700
                 prose-h1:text-[19px] prose-h1:font-semibold prose-h1:mt-5 prose-h1:mb-2 prose-h1:leading-[1.7] prose-h1:text-gray-900
                 prose-h2:text-[17px] prose-h2:font-semibold prose-h2:mt-4 prose-h2:mb-2 prose-h2:leading-[1.7] prose-h2:text-gray-900
                 prose-h3:text-[16px] prose-h3:font-semibold prose-h3:mt-3 prose-h3:mb-1.5 prose-h3:leading-[1.7] prose-h3:text-gray-900
                 prose-h4:text-[15px] prose-h4:font-semibold prose-h4:mt-3 prose-h4:mb-1.5 prose-h4:leading-[1.7] prose-h4:text-gray-900
                 prose-h5:font-semibold prose-h5:mt-2 prose-h5:mb-1 prose-h5:leading-[1.7] prose-h5:text-gray-900
                 prose-h6:font-semibold prose-h6:mt-2 prose-h6:mb-1 prose-h6:leading-[1.7] prose-h6:text-gray-900
                 prose-hr:mt-4 prose-hr:mb-3 prose-hr:border-gray-200
                 prose-ul:pl-6 prose-ul:my-2 prose-ul:leading-[1.75]
                 prose-ol:pl-6 prose-ol:my-2 prose-ol:leading-[1.75]
                 prose-li:my-1 prose-li:leading-[1.75] prose-li:text-gray-700
                 prose-strong:font-semibold prose-strong:text-gray-900
                 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:font-mono prose-code:text-[13.5px]
                 prose-pre:text-gray-900
                 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:my-2 prose-blockquote:italic prose-blockquote:text-gray-600 prose-blockquote:leading-[1.75]
                 prose-a:text-blue-600 hover:prose-a:underline
                 prose-thead:bg-[#f5f5f5]
                 prose-th:px-5 prose-th:py-2.5 prose-th:font-medium prose-th:text-[#636369] prose-th:whitespace-nowrap prose-th:leading-[1.85] prose-th:text-left
                 prose-td:px-5 prose-td:py-4 prose-td:text-gray-900 prose-td:border-t prose-td:border-[#ececed] prose-td:leading-[1.85] prose-td:align-top
                 [&_tbody_tr]:hover:bg-gray-50/40 [&_tbody_tr]:transition-colors
                 [&_thead_th:first-child]:rounded-tl-2xl [&_thead_th:last-child]:rounded-tr-2xl
                 [&_tbody_tr:last-child_td:first-child]:rounded-bl-2xl [&_tbody_tr:last-child_td:last-child]:rounded-br-2xl"
        />
    );
};

export default MarkdownWithCitations;
